import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { loadWorkbook, sheetToMatrix } from "../utils/excelSheet";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {
  EXCEL_PREVIEW_MAX_COLS,
  EXCEL_PREVIEW_MAX_ROWS,
  FILE_PREVIEW_KIND,
  getFilePreviewKind,
} from "../utils/filePreview";

const parseExcelRows = async (blob) => {
  const buffer = await blob.arrayBuffer();
  const workbook = await loadWorkbook(buffer);
  const sheet = workbook.worksheets[0];

  if (!sheet) {
    return { rows: [], truncated: false, totalRows: 0, totalCols: 0 };
  }

  const rawRows = sheetToMatrix(sheet);
  const totalRows = rawRows.length;
  const totalCols = rawRows.reduce((max, row) => Math.max(max, row.length), 0);
  const truncated =
    totalRows > EXCEL_PREVIEW_MAX_ROWS || totalCols > EXCEL_PREVIEW_MAX_COLS;

  const rows = rawRows.slice(0, EXCEL_PREVIEW_MAX_ROWS).map((row) =>
    row.slice(0, EXCEL_PREVIEW_MAX_COLS).map((cell) => (cell == null ? "" : String(cell)))
  );

  return { rows, truncated, totalRows, totalCols };
};

const previewAreaHeightSx = {
  height: { xs: "55vh", sm: "70vh", md: "75vh" },
  minHeight: { xs: 320, sm: 420 },
  maxHeight: "calc(100vh - 180px)",
  width: "100%",
};

const FilePreviewDialog = ({ open, onClose, fileId, fileName, mimeType, onDownload }) => {
  const previewKind = useMemo(() => getFilePreviewKind(fileName, mimeType), [fileName, mimeType]);

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [previewUrl, setPreviewUrl] = useState(null);
  const [textContent, setTextContent] = useState("");
  const [excelData, setExcelData] = useState(null);
  const previewUrlRef = useRef(null);

  const revokeObjectUrl = () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
  };

  useEffect(() => {
    if (!open || !fileId) {
      return undefined;
    }

    let isCancelled = false;

    const loadPreview = async () => {
      revokeObjectUrl();
      setIsLoading(true);
      setErrorMessage("");
      setPreviewUrl(null);
      setTextContent("");
      setExcelData(null);

      if (previewKind === FILE_PREVIEW_KIND.UNSUPPORTED) {
        if (!isCancelled) {
          setIsLoading(false);
        }
        return;
      }

      try {
        const response = await axios.get(`/api/v1/azureblobstorage/${fileId}`, {
          responseType: "blob",
        });

        if (isCancelled) {
          return;
        }

        const blob = response.data;

        if (previewKind === FILE_PREVIEW_KIND.PDF || previewKind === FILE_PREVIEW_KIND.IMAGE) {
          const url = URL.createObjectURL(blob);
          previewUrlRef.current = url;
          setPreviewUrl(url);
          return;
        }

        if (previewKind === FILE_PREVIEW_KIND.TEXT) {
          const text = await blob.text();
          if (!isCancelled) {
            setTextContent(text);
          }
          return;
        }

        if (previewKind === FILE_PREVIEW_KIND.EXCEL) {
          const parsed = await parseExcelRows(blob);
          if (!isCancelled) {
            setExcelData(parsed);
          }
          return;
        }
      } catch (error) {
        console.error("Error loading file preview:", error);
        if (!isCancelled) {
          setErrorMessage("Could not load file preview.");
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    loadPreview().catch(() => undefined);

    return () => {
      isCancelled = true;
      revokeObjectUrl();
    };
  }, [open, fileId, previewKind]);

  const handleClose = () => {
    revokeObjectUrl();
    setPreviewUrl(null);
    setTextContent("");
    setExcelData(null);
    setErrorMessage("");
    setIsLoading(false);
    onClose();
  };

  const handleDownload = () => {
    if (onDownload) {
      onDownload();
    }
  };

  const renderPreviewContent = () => {
    if (isLoading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            ...previewAreaHeightSx,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    if (errorMessage) {
      return (
        <Alert severity="error" sx={{ my: 2 }}>
          {errorMessage}
        </Alert>
      );
    }

    if (previewKind === FILE_PREVIEW_KIND.UNSUPPORTED) {
      return (
        <Box
          sx={{
            minHeight: 200,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            py: 4,
          }}
        >
          <Typography variant="body1" color="text.secondary">
            Preview not available for this file type
          </Typography>
          {mimeType && (
            <Typography variant="body2" color="text.secondary">
              File type: {mimeType}
            </Typography>
          )}
        </Box>
      );
    }

    if (previewKind === FILE_PREVIEW_KIND.PDF && previewUrl) {
      return (
        <Box sx={previewAreaHeightSx}>
          <Box
            component="iframe"
            src={previewUrl}
            title={fileName || "Document preview"}
            sx={{ width: "100%", height: "100%", border: "none", display: "block" }}
          />
        </Box>
      );
    }

    if (previewKind === FILE_PREVIEW_KIND.IMAGE && previewUrl) {
      return (
        <Box
          sx={{
            ...previewAreaHeightSx,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            overflow: "auto",
          }}
        >
          <Box
            component="img"
            src={previewUrl}
            alt={fileName || "Image preview"}
            sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
          />
        </Box>
      );
    }

    if (previewKind === FILE_PREVIEW_KIND.TEXT) {
      return (
        <Box
          sx={{
            ...previewAreaHeightSx,
            overflow: "auto",
            p: 2,
            bgcolor: "grey.50",
            borderRadius: 1,
            border: 1,
            borderColor: "divider",
            boxSizing: "border-box",
          }}
        >
          <Typography
            component="pre"
            variant="body2"
            sx={{ m: 0, whiteSpace: "pre-wrap", wordBreak: "break-word", fontFamily: "inherit" }}
          >
            {textContent}
          </Typography>
        </Box>
      );
    }

    if (previewKind === FILE_PREVIEW_KIND.EXCEL && excelData) {
      const { rows, truncated, totalRows, totalCols } = excelData;

      if (!rows.length) {
        return (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
            This spreadsheet is empty.
          </Typography>
        );
      }

      const headerRow = rows[0] || [];
      const bodyRows = rows.slice(1);

      return (
        <Box>
          {truncated && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              Partial view (showing up to {EXCEL_PREVIEW_MAX_ROWS} rows and {EXCEL_PREVIEW_MAX_COLS}{" "}
              columns of {totalRows} rows and {totalCols} columns).
            </Typography>
          )}
          <TableContainer
            sx={{
              ...previewAreaHeightSx,
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              boxSizing: "border-box",
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {headerRow.map((cell, columnIndex) => (
                    <TableCell key={`header-${columnIndex}`} sx={{ fontWeight: 700 }}>
                      {cell}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {bodyRows.map((row, rowIndex) => (
                  <TableRow key={`row-${rowIndex}`}>
                    {headerRow.map((_, columnIndex) => (
                      <TableCell key={`cell-${rowIndex}-${columnIndex}`}>
                        {row[columnIndex] ?? ""}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      );
    }

    return null;
  };

  const titleId = "file-preview-dialog-title";

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="lg"
      aria-labelledby={titleId}
      PaperProps={{
        sx: {
          maxHeight: "92vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle id={titleId} sx={{ pr: 6, flexShrink: 0 }}>
        {fileName || "Attachment preview"}
      </DialogTitle>
      <DialogContent dividers sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        {renderPreviewContent()}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, flexShrink: 0 }}>
        <Button onClick={handleClose}>Close</Button>
        {onDownload && (
          <Button variant="contained" startIcon={<FileDownloadIcon />} onClick={handleDownload}>
            Download
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default FilePreviewDialog;
